import { useSyncExternalStore } from 'react';
import {
  BRAND_DEFAULTS,
  type BrandRadius,
  type PlatformTheme,
  type TenantTheme,
} from '@up2d8/contracts';
import { brandFontStack, ensureBrandFont } from './brand-fonts';
import { setProductName } from './i18n';

/**
 * De huisstijl toepassen (Stap 43, uitgebreid in MRK-2 voor het platformmerk, ADR-0084).
 *
 * De kleuren komen als CSS-variabelen op `<html>` te staan, bovenóp de tokens uit
 * `packages/ui/src/styles/tokens.css`. Dat werkt omdat de tokens op `:root` staan en een variabele
 * op het element zelf die overschrijft — er is geen enkele component die dit hoeft te weten.
 *
 * **Alles komt van de server** (ADR-0056): de AA-varianten, de verdonkerde zijbalk, en welke laag
 * (bedrijf, platform, UP2D8) een veld levert. De client rekent hier niets uit.
 *
 * `--brand-primary-aa` wordt óók gezet, en dat is het stuk dat makkelijk vergeten wordt: in de
 * app-zone verwijst `--brand-primary` naar `--brand-primary-aa` (ADR-0018). Alleen de eerste zetten
 * zou betekenen dat de medewerkerszone bij de oude kleur blijft terwijl de beheerzone verandert.
 *
 * Twee lagen in de browser:
 *
 * - het **platformmerk** (`setPlatformTheme`), er al vóór het aanmelden (`usePlatformBrand`);
 * - het **thema van een bedrijf** (`applyTenantTheme`), zodra de bedrijfscode of de sessie er is.
 *   Dat is al opgelost bovenop het platformmerk; het wint dus zolang het er is.
 *
 * `clearTenantTheme` haalt het bedrijf weg en zet het platformmerk terug, niet UP2D8.
 */

/** Wat de browser toepast: het publieke platformmerk of het opgeloste thema van een bedrijf. */
export type AppliedTheme = PlatformTheme | TenantTheme;

/** De afrondingen per stand. `rond` staat niet in de tabel: dat zijn de tokens zelf. */
export const RADII: Record<Exclude<BrandRadius, 'rond'>, Record<string, string>> = {
  gemiddeld: { sm: '6px', md: '8px', lg: '10px', xl: '12px' },
  strak: { sm: '2px', md: '3px', lg: '4px', xl: '6px' },
};

/** Elke variabele die hier gezet kan worden; `clear` haalt ze allemaal weg. */
const PROPERTIES = [
  '--brand-primary',
  '--brand-primary-aa',
  '--brand-orange',
  '--brand-orange-aa',
  '--brand-on-primary',
  '--brand-on-accent',
  '--brand-sidebar',
  '--font-heading',
  '--font-body',
  '--radius-sm',
  '--radius-md',
  '--radius-lg',
  '--radius-xl',
] as const;

/**
 * Wat er in `index.html` stond voor de app iets zette: de titel, de kleur van de adresbalk, de naam
 * op het beginscherm. Een thema zonder eigen waarde zet die terug.
 */
let documentDefaults: { title: string; themeColor: string | null; appTitle: string | null } | null =
  null;

function rememberDocumentDefaults() {
  if (documentDefaults) return documentDefaults;
  documentDefaults = {
    title: document.title,
    themeColor: document.querySelector('meta[name="theme-color"]')?.getAttribute('content') ?? null,
    appTitle:
      document.querySelector('meta[name="apple-mobile-web-app-title"]')?.getAttribute('content') ??
      null,
  };
  return documentDefaults;
}

function setMeta(name: string, value: string | null) {
  const meta = document.querySelector(`meta[name="${name}"]`);
  if (meta && value !== null) meta.setAttribute('content', value);
}

/**
 * Het favicon: een `<link rel="icon">` die wij beheren. Zonder favicon van het platform staat er
 * geen, zoals in `index.html` - dan vraagt de browser `/favicon.ico`, zoals altijd.
 */
function setFavicon(url: string | null) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="icon"][data-brand]');
  if (!url) {
    existing?.remove();
    return;
  }
  const link = existing ?? document.createElement('link');
  link.rel = 'icon';
  link.dataset.brand = '';
  link.href = url;
  if (!existing) document.head.appendChild(link);
}

// --- De toestand, voor de schil -------------------------------------------------------------

let platformTheme: PlatformTheme | null = null;
let tenantTheme: TenantTheme | null = null;
let version = 0;
const listeners = new Set<() => void>();

function notify() {
  version += 1;
  for (const listener of listeners) listener();
}

/** Zet alles op `<html>` voor één opgelost thema. */
function paint(theme: AppliedTheme, isTenant: boolean): void {
  const root = document.documentElement;
  const defaults = rememberDocumentDefaults();

  root.style.setProperty('--brand-primary', theme.primary);
  root.style.setProperty('--brand-primary-aa', theme.primaryText);
  root.style.setProperty('--brand-orange', theme.accent);
  root.style.setProperty('--brand-orange-aa', theme.accentEdge);
  root.style.setProperty('--brand-on-primary', theme.onPrimary);
  root.style.setProperty('--brand-on-accent', theme.onAccent);

  /*
    De rest van het palet (tinten, hover, de donkere kleur van de zijbalk, focusringen) leidt de CSS
    af uit deze vier, met `color-mix()` onder `:root[data-brand='custom']` (tokens.css, admin.css,
    floor.css). Alleen als de kleuren afwijken van die van UP2D8 - door het platformmerk óf door het
    bedrijf (ADR-0084): zonder blijft elk scherm exact het ontwerp van UP2D8.

    `--text-on-dark` blijft wit. Het stond hier op `onPrimary`, maar die kleur hoort op de ruwe
    primaire kleur, en die staat nergens meer onder tekst: knoppen gebruiken de AA-variant, waar
    wit altijd op leesbaar is. Met een lichte huisstijl werd de tekst in de zijbalk zo donker op
    donker.
  */
  if (theme.custom) root.dataset.brand = 'custom';
  else delete root.dataset.brand;

  // De zijbalk: al verdonkerd door de server tot wit erop 4,5:1 haalt. Leeg = de afgeleide tint.
  if (theme.sidebar) root.style.setProperty('--brand-sidebar', theme.sidebar);
  else root.style.removeProperty('--brand-sidebar');

  // Lettertypen. De standaard niet als inline stijl: dan blijft tokens.css de enige bron ervan.
  const heading = theme.fontHeading ?? BRAND_DEFAULTS.fontHeading;
  const body = theme.fontBody ?? BRAND_DEFAULTS.fontBody;
  if (heading === BRAND_DEFAULTS.fontHeading) root.style.removeProperty('--font-heading');
  else root.style.setProperty('--font-heading', brandFontStack(heading));
  if (body === BRAND_DEFAULTS.fontBody) root.style.removeProperty('--font-body');
  else root.style.setProperty('--font-body', brandFontStack(body));
  void ensureBrandFont(heading);
  void ensureBrandFont(body);

  // De afronding: `rond` is tokens.css; de andere twee zetten de vier maten. Pill blijft pill.
  const radius = theme.radius ?? BRAND_DEFAULTS.radius;
  for (const size of ['sm', 'md', 'lg', 'xl']) {
    const value = radius === 'rond' ? undefined : RADII[radius][size];
    if (value) root.style.setProperty(`--radius-${size}`, value);
    else root.style.removeProperty(`--radius-${size}`);
  }

  /*
    De tabtitel: de productnaam, en met een bedrijf "Bedrijfsnaam · Productnaam" - een beheerder met
    twee klanten in twee tabbladen ziet zo welk tabblad welk bedrijf is. Is de weergavenaam gelijk
    aan de productnaam, dan maar één keer.

    De naam op het beginscherm is die van het bedrijf (ADR-0071: de medewerkerszone draagt het merk
    van de werkgever); zonder bedrijf de productnaam.
  */
  const productName = theme.productName ?? platformTheme?.productName ?? BRAND_DEFAULTS.productName;
  const tenantName = isTenant && 'displayName' in theme ? theme.displayName : null;
  document.title =
    tenantName && tenantName !== productName ? `${tenantName} · ${productName}` : productName;
  setMeta('apple-mobile-web-app-title', tenantName || productName);
  setProductName(productName);

  setFavicon(theme.faviconUrl ?? platformTheme?.faviconUrl ?? null);

  /*
    Het adresbalk-kleurtje van de browser en het opstartscherm van de PWA. Een bedrijf: zijn
    primaire kleur, zoals sinds Stap 43. Het platformmerk alleen als het van UP2D8 afwijkt; anders
    de kleur uit `index.html`.
  */
  setMeta('theme-color', isTenant || theme.custom ? theme.primary : defaults.themeColor);
}

/** Alles weg: terug naar `index.html` en tokens.css. */
function reset(): void {
  const root = document.documentElement;
  const defaults = rememberDocumentDefaults();
  for (const name of PROPERTIES) root.style.removeProperty(name);
  delete root.dataset.brand;
  document.title = defaults.title;
  setMeta('apple-mobile-web-app-title', defaults.appTitle);
  setMeta('theme-color', defaults.themeColor);
  setFavicon(null);
  setProductName(BRAND_DEFAULTS.productName);
}

/**
 * Het thema van een bedrijf toepassen (het eigen bedrijf na aanmelden, of dat van een bedrijfscode
 * op het aanmeldscherm). Wint van het platformmerk tot `clearTenantTheme`.
 */
export function applyTenantTheme(theme: TenantTheme): void {
  tenantTheme = theme;
  paint(theme, true);
  notify();
}

/**
 * De huisstijl van het bedrijf weer weghalen, en het platformmerk terugzetten.
 *
 * Nodig bij afmelden: zonder dit zou het aanmeldscherm na een logout in de kleuren van het vorige
 * bedrijf blijven staan, en op een gedeeld toestel (Stap 40) is dat de verkeerde indruk. Terug naar
 * het platformmerk en niet naar UP2D8: wie het product onder een andere naam verkoopt, wil na het
 * afmelden niet ineens UP2D8 zien.
 */
export function clearTenantTheme(): void {
  tenantTheme = null;
  if (platformTheme) paint(platformTheme, false);
  else reset();
  notify();
}

/**
 * Het publieke platformmerk instellen (`usePlatformBrand`). Staat er een bedrijf, dan blijft dat
 * staan: het is al opgelost bovenop het platformmerk.
 */
export function setPlatformTheme(theme: PlatformTheme | null): void {
  platformTheme = theme;
  if (tenantTheme) paint(tenantTheme, true);
  else if (theme) paint(theme, false);
  else reset();
  notify();
}

// --- Het merk voor de schil -----------------------------------------------------------------

/** Wat een schil of een aanmeldscherm van het merk tekent. */
export interface ActiveBrand {
  /** Nooit leeg; standaard "UP2D8". */
  productName: string;
  /** Is dit de productnaam van UP2D8? Dan tekent de schil het woordmerk met het middenstuk. */
  isDefaultProduct: boolean;
  productLogoUrl: string | null;
  /** Voor een donkere ondergrond: de zijbalk, het merkvlak van het aanmeldscherm. */
  productLogoDarkUrl: string | null;
  loginTitle: string | null;
  loginText: string | null;
  loginBackgroundUrl: string | null;
  /** Of er nu een bedrijf toegepast is (na aanmelden, of na een geldige bedrijfscode). */
  hasTenant: boolean;
}

let cached: { version: number; brand: ActiveBrand } | null = null;

function snapshot(): ActiveBrand {
  if (cached?.version === version) return cached.brand;
  /*
    Per veld: het bedrijf (al opgelost bovenop het platform), anders het platform, anders UP2D8.
    Het tweede is er voor een server die de velden van MRK-1 nog niet meestuurt.
  */
  const pick = <K extends keyof PlatformTheme>(key: K): PlatformTheme[K] | undefined =>
    (tenantTheme?.[key as keyof TenantTheme] as PlatformTheme[K] | undefined) ??
    platformTheme?.[key];
  const productName = pick('productName') || BRAND_DEFAULTS.productName;
  const brand: ActiveBrand = {
    productName,
    isDefaultProduct: productName === BRAND_DEFAULTS.productName,
    productLogoUrl: pick('productLogoUrl') ?? null,
    productLogoDarkUrl: pick('productLogoDarkUrl') ?? null,
    loginTitle: pick('loginTitle') ?? null,
    loginText: pick('loginText') ?? null,
    loginBackgroundUrl: pick('loginBackgroundUrl') ?? null,
    hasTenant: tenantTheme !== null,
  };
  cached = { version, brand };
  return brand;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Het merk dat nu op het scherm staat; tekent opnieuw zodra een thema wijzigt. */
export function useActiveBrand(): ActiveBrand {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Alleen de productnaam, voor een terugval waar vroeger "UP2D8" stond. */
export function useProductName(): string {
  return useActiveBrand().productName;
}
